import React, { useRef, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';

const CustomIframe = ({
  srcDoc,
  title = "HTML Content",
  sandbox = "allow-scripts allow-same-origin allow-forms allow-popups allow-modals",
  style = {}
}) => {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(0);
  const { theme } = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // CSS variables that match the current platform theme
  const themeVars = isDark ? `
    --color-bg: #1a202c;
    --color-bg-paper: #1f2937;
    --color-bg-elevated: #2d3748;
    --color-text: #f7fafc;
    --color-text-secondary: #cbd5e0;
    --color-primary: #63b3ed;
    --color-primary-light: #90cdf4;
    --color-primary-dark: #4299e1;
    --color-secondary: #FE9D29;
    --color-success: #68D391;
    --color-tertiary: #a855f7;
    --color-border: rgba(255,255,255,0.12);
  ` : `
    --color-bg: #f7fafc;
    --color-bg-paper: #ffffff;
    --color-bg-elevated: #edf2f7;
    --color-text: #2d3748;
    --color-text-secondary: #718096;
    --color-primary: #4299e1;
    --color-primary-light: #63b3ed;
    --color-primary-dark: #3182ce;
    --color-secondary: #ed8936;
    --color-success: #48BB78;
    --color-tertiary: #A855F7;
    --color-border: rgba(0,0,0,0.12);
  `;

  // Platform theme injection style block
  const themeStyleBlock = `<style data-platform-theme>
    :root {
      ${themeVars}
      --font: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
      --font-mono: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace;
      --radius-sm: 4px;
      --radius: 8px;
      --radius-lg: 12px;
    }
    *, *::before, *::after { box-sizing: border-box; }
    html { overflow: hidden; }
    /* Force body background to match platform theme */
    html body {
      background-color: var(--color-bg) !important;
    }
  </style>`;

  // Detect if srcDoc is a full HTML page (has its own <html> structure)
  const isFullHtml = /^\s*<!DOCTYPE|^\s*<html/i.test(srcDoc || '');

  let enhancedSrcDoc;
  if (isFullHtml) {
    // Full HTML page: inject theme vars into its <head>, override body background after </body>
    // 1. Insert our CSS vars at the very start of <head> (available to all subsequent styles)
    // 2. Also add html {overflow:hidden} via late injection to prevent scrollbars
    const withVarsInHead = srcDoc.replace(
      /(<head[^>]*>)/i,
      `$1\n${themeStyleBlock}`
    );
    // If no <head> tag found, prepend the style block
    enhancedSrcDoc = withVarsInHead.includes('data-platform-theme')
      ? withVarsInHead
      : themeStyleBlock + srcDoc;
  } else {
    // HTML fragment: wrap in full document with our base styles
    enhancedSrcDoc = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  ${themeStyleBlock}
  <style>
    body {
      margin: 0; padding: 0; overflow: hidden;
      font-family: var(--font);
      font-size: 16px; line-height: 1.6;
      background-color: var(--color-bg);
      color: var(--color-text);
    }
    h1, h2, h3, h4, h5, h6 { color: var(--color-text); line-height: 1.3; margin-top: 0; }
    a { color: var(--color-primary); text-decoration: none; }
    a:hover { color: var(--color-primary-dark); text-decoration: underline; }
    p { margin-top: 0; }
    img { max-width: 100%; height: auto; display: block; }
    code { font-family: var(--font-mono); font-size: 0.875em; background: var(--color-bg-elevated); border-radius: var(--radius-sm); padding: 2px 6px; }
    pre { font-family: var(--font-mono); background: var(--color-bg-elevated); border-radius: var(--radius); padding: 16px; overflow-x: auto; font-size: 0.875em; }
    pre code { background: none; padding: 0; }
  </style>
</head>
<body>
  ${srcDoc}
</body>
</html>`;
  }

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      try {
        // Получаем доступ к содержимому iframe
        const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
        
        // Функция для обновления высоты
        const updateHeight = () => {
          try {
            const scrollHeight = iframeDocument.documentElement.scrollHeight;
            const bodyHeight = iframeDocument.body.scrollHeight;
            const newHeight = Math.max(scrollHeight, bodyHeight, 0);
            
            setHeight(newHeight);
          } catch (error) {
            console.warn('Не удалось получить высоту iframe:', error);
            setHeight(0);
          }
        };

        // Обновляем высоту после загрузки
        updateHeight();

        // Слушаем изменения размера содержимого через resize событие
        const handleResize = () => {
          updateHeight();
        };

        if (iframeDocument.body) {
          iframeDocument.body.addEventListener('resize', handleResize);
        }

        if (iframeDocument.defaultView) {
          iframeDocument.defaultView.addEventListener('resize', handleResize);
        }

        // Очистка при размонтировании
        return () => {
          if (iframeDocument.body) {
            iframeDocument.body.removeEventListener('resize', handleResize);
          }
          if (iframeDocument.defaultView) {
            iframeDocument.defaultView.removeEventListener('resize', handleResize);
          }
        };
      } catch (error) {
        console.warn('Ошибка при настройке адаптивной высоты iframe:', error);
        setHeight(0);
      }
    };

    iframe.addEventListener('load', handleLoad);

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [srcDoc]);

  return (
    <Box sx={{ mb: 2, overflow: 'hidden' }}>
      <iframe
        ref={iframeRef}
        srcDoc={enhancedSrcDoc}
        style={{
          width: '100%',
          height: `${height}px`,
          border: 'none',
          borderRadius: '8px',
          overflow: 'hidden',
          ...style
        }}
        title={title}
        sandbox={sandbox}
      />
    </Box>
  );
};

export default CustomIframe;
